/**
 * Pure GA4 cookie parsers. No Node, no Next, no fetch. Shared by the
 * Measurement Protocol sender and the browser hidden-field fallback so both
 * sides read `_ga` / `_ga_<stream>` the same way.
 *
 * `_ga` (client_id): `GA1.1.<a>.<b>` → `<a>.<b>`.
 * `_ga_<stream>` (session): GS1 dotted and GS2 `$` (or `%24`) formats.
 */

export type Ga4BrowserSession = { sessionId: string; sessionNumber?: number }

export const GA_CLIENT_ID_FIELD = 'ga_client_id'
export const GA_SESSION_ID_FIELD = 'ga_session_id'

/** Decode a cookie value Google or the browser may have percent-encoded. */
export function decodeCookieValue(raw: string): string {
  let value = raw.trim()
  if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
    value = value.slice(1, -1)
  }
  try {
    value = decodeURIComponent(value)
  } catch {
    // Keep the trimmed value when it is not valid URI encoding.
  }
  return value
}

/** One named cookie from a raw Cookie header. */
export function cookieValueFromHeader(header: string | undefined | null, name: string): string | undefined {
  if (!header || !name) return undefined
  for (const part of header.split(';')) {
    const cut = part.indexOf('=')
    if (cut < 0) continue
    if (part.slice(0, cut).trim() !== name) continue
    return decodeCookieValue(part.slice(cut + 1))
  }
  return undefined
}

export function cookieValueFromStoreOrHeader(
  store: { get(name: string): { value: string } | undefined } | null | undefined,
  header: string | undefined | null,
  name: string,
): string | undefined {
  const fromStore = store?.get(name)?.value
  if (fromStore) return decodeCookieValue(fromStore)
  return cookieValueFromHeader(header, name)
}

/**
 * Parse the GA4 `_ga` cookie value and return the client_id portion.
 * Cookie format is usually `GA1.x.<random>.<timestamp>`. The client_id is the
 * last two numeric dot parts, so `GA1.1.123.456`, `GA1.2.123.456`, and a
 * bare `123.456` all yield `123.456`. Returns null if missing or malformed.
 */
export function clientIdFromGaCookie(cookieValue: string | undefined | null): string | null {
  if (!cookieValue) return null
  const parts = decodeCookieValue(cookieValue).split('.')
  if (parts.length < 2) return null
  const left = parts[parts.length - 2]
  const right = parts[parts.length - 1]
  if (!left || !right || !/^\d+$/.test(left) || !/^\d+$/.test(right)) return null
  return `${left}.${right}`
}

/** A usable `_ga` client_id (`1234567890.1700000000`). Rejects uuids and the raw cookie. */
export function parseGaClientId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!/^\d+\.\d+$/.test(trimmed)) return null
  return trimmed
}

/** Posted or cookie value: the raw `_ga` cookie or a bare `digits.digits`. */
export function parsePostedGaClientId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return clientIdFromGaCookie(value)
}

/** A usable `_ga_<stream>` session_id (unix seconds, 6+ digits). */
export function parseGaSessionId(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const trimmed = String(value).trim()
  if (!/^\d{6,}$/.test(trimmed)) return null
  return trimmed
}

/**
 * The `_ga_<stream>` cookie name for a measurement id: `G-XXXX` -> `_ga_XXXX`.
 */
export function gaStreamCookieName(measurementId: string): string {
  return `_ga_${measurementId.trim().replace(/^G-/i, '')}`
}

const GA_STREAM_COOKIE_NAME = /^_ga_[A-Z0-9]+$/

/**
 * First usable `_ga_<stream>` session from a Cookie header. Used when the
 * measurement id is missing (CI, a request without NEXT_PUBLIC_GA4_*) so we
 * still read the stream cookie the browser set.
 */
export function sessionFromCookieHeader(header: string | undefined | null): Ga4BrowserSession | null {
  if (!header) return null
  for (const part of header.split(';')) {
    const cut = part.indexOf('=')
    if (cut < 0) continue
    const name = part.slice(0, cut).trim()
    if (!GA_STREAM_COOKIE_NAME.test(name)) continue
    const parsed = sessionFromGaStreamCookie(part.slice(cut + 1))
    if (parsed) return parsed
  }
  return null
}

type CookieListStore = {
  get(name: string): { value: string } | undefined
  getAll?: () => Array<{ name: string; value: string }>
}

/** First usable `_ga_<stream>` session from cookies().getAll() when present. */
export function sessionFromCookieStore(store: CookieListStore | null | undefined): Ga4BrowserSession | null {
  const all = typeof store?.getAll === 'function' ? store.getAll() : []
  for (const cookie of all) {
    if (!cookie || !GA_STREAM_COOKIE_NAME.test(cookie.name)) continue
    const parsed = sessionFromGaStreamCookie(cookie.value)
    if (parsed) return parsed
  }
  return null
}

function parseGsDollarBody(body: string): Ga4BrowserSession | null {
  let sessionId: string | null = null
  let sessionNumber: number | undefined
  for (const part of body.split('$')) {
    if (!part) continue
    const k = part.charAt(0)
    const v = part.slice(1)
    if (k === 's' && /^\d{6,}$/.test(v)) sessionId = v
    else if (k === 'o' && /^\d+$/.test(v)) sessionNumber = Number(v)
  }
  return sessionId ? { sessionId, ...(sessionNumber ? { sessionNumber } : {}) } : null
}

/**
 * Parse the session the Google tag keeps in the `_ga_<stream>` cookie.
 *
 * WHY. A Measurement Protocol event without the browser's `session_id` is a
 * session of its own in GA4: no landing page, no source, hostname (not set),
 * channel Unassigned. Google's rule: send the same client_id AND session_id
 * the web tag uses and the event joins that session.
 *
 * Formats seen in the wild:
 *   GS1.1.<session_id>.<session_number>.<engaged>.<last_hit>.<...>
 *   GS2.1.s<session_id>$o<session_number>$g<engaged>$t<last_hit>$...
 *   GS1.1.s<session_id>$o<session_number>$...  (GS1 prefix, GS2 body)
 *   The same with `$` percent-encoded as `%24`.
 */
export function sessionFromGaStreamCookie(value: string | undefined | null): Ga4BrowserSession | null {
  const raw = typeof value === 'string' ? decodeCookieValue(value) : ''
  if (!raw) return null
  const gs1 = /^GS1\.\d+\.(\d{6,})\.(\d+)(?:\.|$)/.exec(raw)
  if (gs1) return { sessionId: gs1[1], sessionNumber: Number(gs1[2]) }
  const dollar = /^(GS[12]\.\d+)\.(.+)$/.exec(raw)
  if (dollar && dollar[2].includes('$')) return parseGsDollarBody(dollar[2])
  return null
}
